import axios from 'axios'
import { normalizePhoneAvailability } from '@core/utils/time'

const loadedUserData = JSON.parse(localStorage.getItem('userData'))
const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

const createDefaultPhoneAvailability = () => ({
  availability: daysOfWeek.map(day => ({
    day,
    slots: [],
  })),
})

const getDeviceTwin = async equipment => {
  const response = await axios.get(
    `${process.env.VUE_APP_FUNCTION_BASE_URL}/proxy/ping`,
    {
      params: {
        code: process.env.VUE_APP_FUNCTION_PROXY_KEY,
        equipments: equipment,
      },
    },
  )
  return {
    agentAvailable: response.data[0].properties?.desired?.agentAvailable,
    phoneCallEnabled: response.data[0].properties?.desired?.enablePhoneCall,
    phoneAvailability: response.data[0].properties?.desired?.phoneAvailability?.availability || [],
    phoneNumber: response.data[0].properties?.desired?.phoneAvailability?.phone || null,
    timeZone: response.data[0].properties?.desired?.phoneAvailability?.timezone || null,
  }
}

const state = {
  agentAvailable: false,
  phoneCallEnabled: false,
  phoneAvailability: createDefaultPhoneAvailability().availability,
  phoneNumber: null,
  timeZone: null,
}

const mutations = {
  SET_AGENT_AVAILABLE(localState, value) {
    localState.agentAvailable = value
  },
  SET_PHONE_CALL_ENABLED(localState, value) {
    localState.phoneCallEnabled = value
  },
  SET_PHONE_AVAILABILITY(localState, value) {
    localState.phoneAvailability = normalizePhoneAvailability(value)
  },
  UPDATE_PHONE_AVAILABILITY(localState, { day, slots }) {
    const dayAvailability = localState.phoneAvailability.find(item => item.day === day)
    const normalizedSlots = normalizePhoneAvailability([{ day, slots }])[0]?.slots || []

    if (!dayAvailability) {
      localState.phoneAvailability.push({
        day,
        slots: normalizedSlots,
      })
      return
    }
    dayAvailability.slots = normalizedSlots
  },
  UPDATE_PHONE_NUMBER(localState, value) {
    localState.phoneNumber = value
  },
  UPDATE_TIME_ZONE(localState, value) {
    localState.timeZone = value
  },
}

const actions = {
  async fetchDeviceTwin({ rootState, commit }, equipmentSerial = null) {
    const equipment = equipmentSerial || rootState.equipment.equipments[0]?.serialNumber
    if (!equipment) {
      return false
    }

    const twin = await getDeviceTwin(equipment)

    commit('SET_AGENT_AVAILABLE', twin.agentAvailable)
    commit('SET_PHONE_CALL_ENABLED', twin.phoneCallEnabled)
    commit('SET_PHONE_AVAILABILITY', createDefaultPhoneAvailability().availability)
    twin.phoneAvailability.forEach(dayAvailability => {
      commit('UPDATE_PHONE_AVAILABILITY', {
        day: dayAvailability.day,
        slots: dayAvailability.slots,
      })
    })
    commit('UPDATE_PHONE_NUMBER', twin.phoneNumber)
    commit('UPDATE_TIME_ZONE', twin.timeZone)
    return true
  },

  async toggleAgentAvailable({ commit, state: localState, dispatch }) {
    const previousValue = localState.agentAvailable
    const newValue = !localState.agentAvailable
    commit('SET_AGENT_AVAILABLE', newValue)
    const success = await dispatch('updateDeviceTwin', { agentAvailable: newValue })
    if (!success) {
      commit('SET_AGENT_AVAILABLE', previousValue)
    }
    return success
  },
  async updatePhoneAvailability({ commit, state: localState, dispatch }, { day, slots }) {
    const previousAvailability = localState.phoneAvailability.find(item => item.day === day)?.slots || []
    commit('UPDATE_PHONE_AVAILABILITY', { day, slots })
    const success = await dispatch('updateDeviceTwin', { phoneAvailability: localState.phoneAvailability })
    if (!success) {
      commit('UPDATE_PHONE_AVAILABILITY', { day, slots: previousAvailability })
    }
    return success
  },
  async updatePhoneNumber({ commit, state: localState, dispatch }, phoneNumber) {
    const previousPhoneNumber = localState.phoneNumber
    commit('UPDATE_PHONE_NUMBER', phoneNumber)
    const success = await dispatch('updateDeviceTwin', { phoneNumber })
    if (!success) {
      commit('UPDATE_PHONE_NUMBER', previousPhoneNumber)
    }
    return success
  },
  updateTimeZone({ commit, state: localState, dispatch }, timeZone) {
    const previousTimeZone = localState.timeZone
    commit('UPDATE_TIME_ZONE', timeZone)
    dispatch('updateDeviceTwin', { timeZone })
      .catch(() => {
        commit('UPDATE_TIME_ZONE', previousTimeZone)
      })
  },
  async updateDeviceTwin({ rootState }, payload = {}) {
    const {
      agentAvailable = null,
      phoneCallEnabled = null,
      phoneAvailability = null,
      phoneNumber = null,
      timeZone = null,
    } = payload || {}

    if (agentAvailable === null && phoneCallEnabled === null && phoneAvailability === null && phoneNumber === null && timeZone === null) {
      return true // No update needed
    }
    const userData = JSON.parse(localStorage.getItem('userData')) || loadedUserData
    const data = {}
    const phoneAvailabilityData = {
      availability: normalizePhoneAvailability(rootState.agent.phoneAvailability),
      phone: rootState.agent.phoneNumber,
      timezone: rootState.agent.timeZone,
    }
    if (agentAvailable != null) {
      data.agentAvailable = agentAvailable
    } else if (phoneCallEnabled != null) {
      data.enablePhoneCall = phoneCallEnabled
    } else {
      if (phoneAvailability != null) {
        phoneAvailabilityData.availability = normalizePhoneAvailability(phoneAvailability)
      } else if (phoneNumber != null) {
        phoneAvailabilityData.availability = normalizePhoneAvailability(rootState.agent.phoneAvailability)
      } else if (timeZone != null) {
        phoneAvailabilityData.timezone = timeZone
      }
      data.phoneAvailability = phoneAvailabilityData
    }
    const equipments = rootState.equipment.equipments.map(item => item.serialNumber).join(',')
    const url = `${process.env.VUE_APP_FUNCTION_BASE_URL}/proxy/update?code=${process.env.VUE_APP_FUNCTION_PROXY_UPDATE_KEY}&equipments=${equipments}&username=${userData.username}&client=${userData.clientName}`
    const maxAttempts = 3
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        await axios.post(url, data)
        return true // Success
      } catch (error) {
        if (attempt === maxAttempts) {
          return false // Failed after max attempts
        }
      }
    }
    return false // Return false if all attempts fail
  },
  updateDeviceTwinSync({ rootState }, value) {
    const userData = JSON.parse(localStorage.getItem('userData')) || loadedUserData
    const data = { agentAvailable: value }
    const equipments = rootState.equipment.equipments.map(item => item.serialNumber).join(',')
    const url = `${process.env.VUE_APP_FUNCTION_BASE_URL}/proxy/update?code=${process.env.VUE_APP_FUNCTION_PROXY_UPDATE_KEY}&equipments=${equipments}&username=${userData.username}&client=${userData.clientName}`
    const payload = JSON.stringify(data)

    const maxAttempts = 3
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const success = navigator.sendBeacon(url, payload)
        if (success) {
          return true // Success
        } else {
          fetch(url, {
            method: 'POST',
            body: payload,
            headers: {
              'Content-Type': 'application/json',
            },
          }).then(() => true) // Success
            .catch(() => {
              if (attempt === maxAttempts) {
                return false // Failed after max attempts
              }
              return false // Add a return statement
            })
        }
      } catch (error) {
        if (attempt === maxAttempts) {
          return false // Failed after max attempts
        }
      }
    }
    return false // Return false if all attempts fail
  },
}

const getters = {
  agentAvailable: localState => localState.agentAvailable,
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
